<script setup>
/**
 * @description 下载资料通用列表项，统一下载中心与商品详情的文件展示和响应式操作入口。
 * @emits preview - 查看资料。
 * @emits download - 下载资料。
 * @emits shared - 分享成功。
 */
import { computed, ref } from 'vue'
import {
  IconClockCircle,
  IconDownload,
  IconEye,
  IconLoading,
  IconMoreVertical,
  IconShareInternal,
} from '@arco-design/web-vue/es/icon'
import SharePopup from '~/components/base/SharePopup.vue'
import { BaseMessage } from '~/utils/baseMessage'

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  iconSrc: {
    type: String,
    default: '',
  },
  dateText: {
    type: String,
    default: '',
  },
  downloadCount: {
    type: [String, Number],
    default: '',
  },
  previewCount: {
    type: [String, Number],
    default: '',
  },
  fileSize: {
    type: String,
    default: '',
  },
  canPreview: {
    type: Boolean,
    default: false,
  },
  canDownload: {
    type: Boolean,
    default: false,
  },
  downloading: {
    type: Boolean,
    default: false,
  },
  shareUrl: {
    type: String,
    default: '',
  },
  mainAction: {
    type: String,
    default: '',
    validator: (value) => ['', 'preview', 'download'].includes(value),
  },
  desktopActionMode: {
    type: String,
    default: 'menu',
    validator: (value) => ['menu', 'buttons'].includes(value),
  },
  showMainActionButton: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['preview', 'download', 'shared'])
const mobileMenuVisible = ref(false)

const isMainClickable = computed(() => Boolean(props.mainAction))
const showPreviewAction = computed(
  () => props.canPreview && (props.showMainActionButton || props.mainAction !== 'preview')
)
const showDownloadAction = computed(
  () => props.canDownload && (props.showMainActionButton || props.mainAction !== 'download')
)
const hasActions = computed(
  () => showPreviewAction.value || showDownloadAction.value || Boolean(props.shareUrl)
)
const hasDownloadCount = computed(
  () =>
    props.downloadCount !== '' && props.downloadCount !== null && props.downloadCount !== undefined
)
const hasPreviewCount = computed(
  () => props.previewCount !== '' && props.previewCount !== null && props.previewCount !== undefined
)
const shareCopyText = computed(() => {
  const title = props.title.trim()
  return title ? `${title}，文件详情链接：${props.shareUrl}` : props.shareUrl
})
const handleMainAction = () => {
  if (!props.mainAction) return
  emit(props.mainAction)
}

const handleMobileMenuVisibleChange = (visible) => {
  mobileMenuVisible.value = visible
}

const handleMobileAction = (action) => {
  mobileMenuVisible.value = false
  emit(action)
}

const handleMobileShare = async () => {
  mobileMenuVisible.value = false
  if (!import.meta.client || !props.shareUrl) return

  try {
    if (navigator.share) {
      await navigator.share({
        title: props.title || '下载资料',
        url: props.shareUrl,
      })
    } else if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(shareCopyText.value)
      BaseMessage.success('已复制链接')
    } else {
      BaseMessage.info('请复制页面链接进行分享')
    }
    emit('shared')
  } catch (error) {
    if (error?.name !== 'AbortError') {
      BaseMessage.error('分享失败，请稍后重试')
    }
  }
}
</script>

<template>
  <article class="download-file-item">
    <div
      class="download-file-item__main"
      :class="{ 'download-file-item__main--clickable': isMainClickable }"
      :role="isMainClickable ? 'button' : undefined"
      :tabindex="isMainClickable ? 0 : undefined"
      @click="handleMainAction"
      @keydown.enter.prevent="handleMainAction"
      @keydown.space.prevent="handleMainAction"
    >
      <div class="download-file-item__icon">
        <img :src="iconSrc" :alt="title || '文件图标'" />
      </div>
      <div class="download-file-item__info">
        <h3 class="download-file-item__title" :title="title">
          <slot name="title">{{ title }}</slot>
        </h3>
        <div class="download-file-item__meta">
          <span v-if="dateText" class="download-file-item__meta-item">
            <IconClockCircle />
            {{ dateText }}
          </span>
          <span v-if="hasDownloadCount" class="download-file-item__meta-item">
            <IconDownload />
            {{ downloadCount }}
          </span>
          <span v-if="hasPreviewCount" class="download-file-item__meta-item">
            <IconEye />
            {{ previewCount }}
          </span>
          <span v-if="fileSize" class="download-file-item__meta-item download-file-item__size">
            {{ fileSize }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="hasActions && desktopActionMode === 'buttons'"
      class="download-file-item__desktop-actions"
    >
      <button
        v-if="showPreviewAction"
        type="button"
        class="download-file-item__action-btn"
        aria-label="查看"
        title="查看"
        @click="emit('preview')"
      >
        <IconEye />
      </button>
      <button
        v-if="showDownloadAction"
        type="button"
        class="download-file-item__action-btn"
        :class="{ 'download-file-item__action-btn--loading': downloading }"
        :aria-busy="downloading"
        :disabled="downloading"
        aria-label="下载"
        title="下载"
        @click="emit('download')"
      >
        <IconLoading v-if="downloading" class="download-file-item__loading-icon" />
        <IconDownload v-else />
      </button>
      <SharePopup
        v-if="shareUrl"
        :share-url="shareUrl"
        :share-title="title"
        @share="emit('shared')"
      >
        <template #trigger>
          <button
            type="button"
            class="download-file-item__action-btn"
            aria-label="分享资料"
            title="分享资料"
          >
            <IconShareInternal />
          </button>
        </template>
      </SharePopup>
    </div>

    <div
      v-if="hasActions"
      class="download-file-item__menu-actions"
      :class="{ 'download-file-item__menu-actions--desktop': desktopActionMode === 'menu' }"
    >
      <a-popover
        trigger="click"
        position="br"
        :popup-visible="mobileMenuVisible"
        :popup-container="'body'"
        :popup-offset="6"
        content-class="download-file-item-menu-popover"
        :content-style="{ padding: 0, borderRadius: '6px' }"
        @popup-visible-change="handleMobileMenuVisibleChange"
      >
        <button
          type="button"
          class="download-file-item__more-btn"
          aria-label="更多操作"
          :aria-expanded="mobileMenuVisible"
        >
          <icon-more-vertical />
        </button>
        <template #content>
          <div class="download-file-item-menu" role="menu">
            <button
              v-if="showPreviewAction"
              type="button"
              class="download-file-item-menu__item"
              role="menuitem"
              @click="handleMobileAction('preview')"
            >
              查看
            </button>
            <button
              v-if="showDownloadAction"
              type="button"
              class="download-file-item-menu__item"
              :disabled="downloading"
              role="menuitem"
              @click="handleMobileAction('download')"
            >
              下载
            </button>
            <button
              v-if="shareUrl"
              type="button"
              class="download-file-item-menu__item"
              role="menuitem"
              @click="handleMobileShare"
            >
              转发
            </button>
          </div>
        </template>
      </a-popover>
    </div>
  </article>
</template>

<style lang="scss" scoped>
.download-file-item {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 0 0 24px;
  border-bottom: 1px solid $border-light-color;

  &__main {
    display: flex;
    flex: 1;
    align-items: center;
    gap: 12px;
    min-width: 0;
    color: inherit;

    &--clickable {
      cursor: pointer;

      &:hover .download-file-item__title {
        color: $primary-color;
      }

      &:focus-visible {
        outline: 2px solid rgba($primary-color, 0.35);
        outline-offset: 3px;
        border-radius: $radius-6;
      }
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    flex-shrink: 0;
    border-radius: $radius-6;

    img {
      display: block;
      width: 44px;
      height: 44px;
      object-fit: contain;
    }
  }

  &__info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
  }

  &__title {
    display: block;
    width: fit-content;
    max-width: 100%;
    margin: 0 0 4px;
    color: $text-title-color;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    transition: color 0.3s;
    @include text-ellipsis(1);
    // 保留标题中的连续空格，同时维持单行省略效果。
    white-space: pre;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
    overflow: hidden;
    color: $text-subtitle-color;
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;
  }

  &__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;

    :deep(.arco-icon) {
      font-size: 14px;
    }
  }

  &__size {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__desktop-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  &__action-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background-color: transparent;
    color: $text-content-color;
    cursor: pointer;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:hover,
    &:focus-visible,
    &--loading {
      background-color: $bg-white;
      color: $primary-color;
    }

    &:focus-visible {
      outline: 2px solid rgba($primary-color, 0.35);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.7;
    }

    :deep(.arco-icon) {
      font-size: 18px;
    }
  }

  &__loading-icon {
    animation: arco-loading-circle 1s linear infinite;
  }

  &__menu-actions {
    display: none;
    position: relative;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    flex-shrink: 0;

    &--desktop {
      display: flex;
    }
  }

  &__more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: $radius-6;
    background: transparent;
    color: $text-content-color;
    cursor: pointer;

    &:hover {
      background-color: rgba($primary-color, 0.1);
      color: $primary-color;
    }

    :deep(.arco-icon) {
      font-size: 18px;
    }
  }
}

@include respond-to-max('lg') {
  .download-file-item {
    gap: 8px;
    padding: 0 0 12px;

    &__main {
      gap: 8px;
    }

    &__icon {
      width: 36px;
      height: 36px;

      img {
        width: 36px;
        height: 36px;
      }
    }

    &__title {
      margin-bottom: 2px;
      font-size: 14px;
      font-weight: 500;
      line-height: 22px;
    }

    &__meta {
      gap: 8px;
      font-size: 12px;
      line-height: 18px;
    }

    &__meta-item {
      gap: 4px;

      :deep(.arco-icon) {
        font-size: 12px;
      }
    }

    &__desktop-actions {
      display: none;
    }

    &__menu-actions {
      display: flex;
      width: 16px;
      height: 36px;
    }

    &__more-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 22px;
      padding: 0;
      border: 0;
      background: transparent;
      color: $text-content-color;
      cursor: pointer;

      :deep(.arco-icon) {
        font-size: 16px;
      }
    }
  }
}
</style>

<style lang="scss">
.download-file-item-menu-popover {
  padding: 0 !important;
  border-radius: $radius-6 !important;
  box-shadow: 0 2px 8px rgba($color-black, 0.1) !important;
}

.download-file-item-menu {
  width: 96px;
  overflow: hidden;
  border-radius: $radius-6;
  background: $bg-white;

  &__item {
    display: flex;
    position: relative;
    align-items: center;
    width: 100%;
    height: 44px;
    padding: 12px;
    border: 0;
    background: transparent;
    color: $text-title-color;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: left;
    cursor: pointer;

    &:not(:last-child)::after {
      position: absolute;
      right: 12px;
      bottom: 0;
      left: 12px;
      height: 1px;
      background: $border-light-color;
      content: '';
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
}
</style>
